/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useDispatch } from 'react-redux';
import { fetchPro } from 'resources/Pro/ProData';
import { useDataFetcherV2 } from 'components/Common/Data/useDataFetcher';
import { isPresent } from 'utils/isPresent';
import { identify } from 'components/Services/Analytics/AnalyticsService';
import { useTimeout } from 'hooks/useTimeout';
import { LocalStorage } from 'components/Common/Utils/local_storage';
import { v4 as uuidv4} from "uuid";
import { formattedFlags } from './Data';

const commonReactOptions = {
  useCamelCaseFlagKeys: false,
};

function WaitForLDInitialize({ children }: { children: JSX.Element }) {
  const ldClient = useLDClient();
  const dispatch = useDispatch();
  const flags = useFlags();
  const timerDone = useTimeout();

  useEffect(() => {
    if (ldClient && isPresent(flags)) {
      dispatch(identify({ ld_experiments: formattedFlags(flags) }));
    }
  }, [ldClient, dispatch, flags]);

  const ldInitializedOrTimedOut = timerDone || Boolean(ldClient);
  return ldInitializedOrTimedOut ? children : null;
}

type Props = {
  employee: {
    user_id: string;
    first_name: string;
    last_name: string;
  };
  children: JSX.Element;
}

export function LaunchDarklyAdminProvider({ employee, children }: Props): JSX.Element {
  const { LAUNCH_DARKLY_ADMIN_PANEL_KEY } = window;

  const config = {
    clientSideID: LAUNCH_DARKLY_ADMIN_PANEL_KEY,
    reactOptions: {
      ...commonReactOptions,
    },
  };

  const userConfig = isPresent(employee) && {
    user: {
      key: employee.user_id,
      firstName: employee.first_name,
      lastName: employee.last_name,
    },
  };

  return (
    <LDProvider deferInitialization {...userConfig} {...config}>
      <WaitForLDInitialize>
        {children}
      </WaitForLDInitialize>
    </LDProvider>
  );
}

export function LaunchDarklyAppProvider({ children }: { children: JSX.Element }): JSX.Element {
  const [, pro] = useDataFetcherV2(fetchPro);
  const { LAUNCH_DARKLY_JS_KEY } = window;

  const config = {
    // eslint-disable-next-line no-undef
    clientSideID: LAUNCH_DARKLY_JS_KEY,
    reactOptions: {
      ...commonReactOptions,
    },
  };

  const userConfig = isPresent(pro) && {
    user: {
      key: pro.id,
      email: pro.email,
      custom: {
        orgUuid: pro.organization_uuid,
        orgCreatedAt: pro.organization_created_at,
      },
    },
  };

  return (
    <LDProvider deferInitialization {...userConfig} {...config}>
      <WaitForLDInitialize>
        {children}
      </WaitForLDInitialize>
    </LDProvider>
  );
}

export function AnonymousLaunchDarklyAppProvider({ children }: { children: JSX.Element }): JSX.Element {
  const { LAUNCH_DARKLY_JS_KEY } = window;
  const config = {
    // eslint-disable-next-line no-undef
    clientSideID: LAUNCH_DARKLY_JS_KEY,
    reactOptions: {
      ...commonReactOptions,
    },
  };

  let uniqueSecondaryKey='';
  if (LocalStorage.getItem('uniqueSecondaryKey')) {
    uniqueSecondaryKey = LocalStorage.getItem('uniqueSecondaryKey');
  } else {
    uniqueSecondaryKey = uuidv4()
    LocalStorage.setItem('uniqueSecondaryKey', uniqueSecondaryKey);
  }

  const userConfig = {
    user: {
      key: 'magic-link-web',
      secondary: uniqueSecondaryKey,
    },
  };

  return (
    <LDProvider {...userConfig} {...config}>
      <WaitForLDInitialize>
        {children}
      </WaitForLDInitialize>
    </LDProvider>
  );
}
